import React from "react";
import {
  FooterContainer,
  FooterWrapper,
  FooterLinksContainer,
  FooterLinkTitle,
  SocialMedia,
} from "./FooterElements";
import Qrcode from "./Qrcode";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { FaEnvelope, FaPhone, FaMapMarkerAlt } from "react-icons/fa";

import facebookIcon from "../../images/facebook.png";
import instagramIcon from "../../images/instagram.png";

const MainContentContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 16px;

  @media screen and (max-width: 992px) {
    gap: 20px;
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 24px;
    padding: 0 12px;
  }
`;

const ContactSection = styled.div`
  flex: 0 0 auto;
  min-width: 220px;
  padding-top: 35px;

  @media screen and (max-width: 768px) {
    width: 100%;
    max-width: 320px;
    padding-top: 10px;
    text-align: center;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #ccc;

  a {
    color: #ccc;
    text-decoration: none;
    transition: color 0.2s ease;
    &:hover { color: #01bf71; }
  }

  @media screen and (max-width: 768px) {
    justify-content: center;
  }
`;

const GreenIcon = styled.span`
  color: #01bf71;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const SocialsSection = styled.div`
  flex: 0 0 auto;
  min-width: 140px;
  padding-top: 35px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;

  @media screen and (max-width: 768px) {
    width: 100%;
    max-width: 320px;
    padding-top: 0;
    align-items: center;
    margin-bottom: 20px;
  }
`;

const FollowTitle = styled.div`
  color: white;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 8px;
  text-align: center;

  @media screen and (max-width: 480px) {
    font-size: 15px;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
  margin-top: 4px;

  @media screen and (max-width: 768px) {
    align-items: flex-start;
    width: auto;
  }
`;

const SocialIconLink = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #cfcfcf;
  font-size: 14px;
  transition: color 0.25s ease;

  &:hover {
    color: #01bf71;
  }
`;

const IconCircle = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  border: none;
  color: inherit;
  font-size: inherit;
  flex-shrink: 0;
  width: auto;
  height: auto;
  border-radius: 0;
  transition: none;
`;

const SocialIconImage = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

const QrcodeSection = styled.div`
  flex: 0 0 auto;

  @media screen and (max-width: 768px) {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

const BottomBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    text-align: center;
  }
`;

const LogoWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;

  @media screen and (max-width: 768px) {
    justify-content: center;
  }
`;

const CopyrightWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
  text-align: center;

  @media screen and (max-width: 768px) {
    white-space: normal;
    font-size: 12px;
  }
`;

const RightSpacer = styled.div`
  flex: 1;

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const socials = [
  {},
  {
    href: "https://www.facebook.com/profile.php?id=61588588681819",
    icon: <SocialIconImage src={facebookIcon} alt="Facebook" />,
    label: "Facebook",
  },
  {
    href: "https://www.instagram.com/cli_claim/",
    icon: <SocialIconImage src={instagramIcon} alt="Instagram" />,
    label: "Instagram",
  },
  {
    href: "https://www.tiktok.com/@cliclaim",
    icon: (
      <SocialIconImage
        src="https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/tiktok.svg"
        alt="TikTok"
        style={{ filter: "invert(1)" }}
      />
    ),
    label: "TikTok",
  },
];

const Footer = ({ isRTL }) => {
  const { t } = useTranslation();

  Footer.propTypes = { isRTL: PropTypes.bool.isRequired };

  return (
    <FooterContainer>
      <FooterWrapper>

        <FooterLinksContainer>
          <MainContentContainer>

            {/* ── Contactez-nous ── */}
            <ContactSection>
              <FooterLinkTitle style={{ color: "white" }}>
                {t("footer.contact")}
              </FooterLinkTitle>
              <ContactInfo>
                <ContactItem>
                  <GreenIcon><FaPhone style={{ transform: "scaleX(-1)" }} /></GreenIcon>
                  <a href="tel:+21653278286">+216 53 27 82 86</a>
                </ContactItem>
                <ContactItem>
                  <GreenIcon><FaPhone style={{ transform: "scaleX(-1)" }} /></GreenIcon>
                  <a href="tel:+33753157294">+33 7 53 15 72 94</a>
                </ContactItem>
                <ContactItem>
                  <GreenIcon><FaPhone style={{ transform: "scaleX(-1)" }} /></GreenIcon>
                  <a href="tel:+966552526767">+966 55 25 26 767</a>
                </ContactItem>
                <ContactItem>
                  <GreenIcon><FaEnvelope /></GreenIcon>
                  <a href="mailto:cliclaim@cliclaim.com">cliclaim@cliclaim.com</a>
                </ContactItem>
                <ContactItem style={{ alignItems: "flex-start" }}>
                  <GreenIcon style={{ marginTop: "2px" }}><FaMapMarkerAlt /></GreenIcon>
                  <div>
                    <div style={{ color: "#aaa", fontSize: "12px", marginBottom: "2px" }}>Tunis, Tunisie</div>
                    <a href="https://maps.google.com/?q=15+CHEDLI+GTARI+EL+MENZEH+9,+Tunis,+1013" target="_blank" rel="noopener noreferrer">15 Chedli Gtari, El Menzah 9, Tunis 1013</a>
                  </div>
                </ContactItem>
                <ContactItem style={{ alignItems: "flex-start" }}>
                  <GreenIcon style={{ marginTop: "2px" }}><FaMapMarkerAlt /></GreenIcon>
                  <div>
                    <div style={{ color: "#aaa", fontSize: "12px", marginBottom: "2px" }}>Riyad, Arabie Saoudite</div>
                    <a href="https://maps.google.com/?q=Al+Miqdad,+Al+Rafiah,+Shumaisi,+Riyad+11373" target="_blank" rel="noopener noreferrer">Al Miqdad, Al Rafiah, Shumaisi, Riyad 11373</a>
                  </div>
                </ContactItem>
              </ContactInfo>
            </ContactSection>

            {/* ── Suivez-nous ── */}
            <SocialsSection>
              <FollowTitle>
                {t("footer.follow")}
              </FollowTitle>
              <SocialIcons>
                {socials.map(({ href, icon, label }) => (
                  <SocialIconLink
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                  >
                    <IconCircle className="icon-circle">{icon}</IconCircle>
                    <span>{label}</span>
                  </SocialIconLink>
                ))}
              </SocialIcons>
            </SocialsSection>

            {/* ── QR codes + stores ── */}
            <QrcodeSection>
              <Qrcode />
            </QrcodeSection>

          </MainContentContainer>
        </FooterLinksContainer>

        {/* ── Barre du bas ── */}
        <SocialMedia>
          <BottomBar>
            <LogoWrapper />
            <CopyrightWrapper>
              <>
                <span style={{ color: "#18e68e" }}>C</span>li
                <span style={{ color: "#18e68e" }}>C</span>laim © {new Date().getFullYear()} {t("footer.title")}.
              </>
            </CopyrightWrapper>
            <RightSpacer />
          </BottomBar>
        </SocialMedia>

      </FooterWrapper>
    </FooterContainer>
  );
};

export default Footer;
